// Imported from their own modules rather than the barrels: neither enum is re-exported by
// `lib/enumerators` at runtime in the version range this repo pins, so the barrel import
// type-checks and then yields `undefined` at runtime. audit-web has no working type gate to
// catch that, so it surfaces only as a swallowed TypeError at the call site.
import { AppIDEnum } from '@nimbly-technologies/nimbly-common/lib/enumerators/app';
import { AppTypeEnum } from '@nimbly-technologies/nimbly-common/lib/models/entities/userEmailQueue';
import firebase from 'firebase';

import { NIMBLY_API_USER_LOGGED_DEVICE } from '../constants/api';

/**
 * The signed-in user's device identity, held in Mongo by api-users.
 *
 * ADR-065 Phase 4. This replaces the three RTDB `/user/{uid}` writes (login in Landing,
 * logout in App and Settings) and the one-shot RTDB read in the profile saga, so the web
 * client stops touching the `/user` node entirely.
 *
 * Two things worth knowing about the endpoint before changing anything here:
 *
 * - **There is one device slot, and `appID` does not select it.** `AppIDEnum` has a single
 *   member, and api-users' upsert always writes `nimblyApp` regardless of the `appID` in
 *   the path -- the path parameter only picks which previous uuid its replacement telemetry
 *   compares against. So web shares the mobile app's slot. That is deliberate (decision,
 *   2026-08-11) and is **parity with today**, not a new behaviour: web already writes the
 *   same `user.uuid`/`user.deviceInfo` fields that the mobile login path writes.
 * - **The upsert replaces the whole slot**, so a web write overwrites `deviceInfo` too.
 *   Also parity: `Landing.tsx` already wrote `deviceInfo` wholesale at login.
 *
 * `nimblyAppType` has no web member either, so web reports `NIMBLY_APP`. It is a telemetry
 * label rather than something the device check reads.
 */
const APP_ID = AppIDEnum.NIMBLY_APP;

export interface WebDeviceProfile {
  uuid: string;
  hasNotch?: boolean;
  apiLevel?: number;
  binaryVersion?: string;
  carrier?: string;
  locale?: string;
  phoneBrand?: string;
  phoneModel?: string;
  phoneOS?: string;
  phoneOSVersion?: string;
}

/**
 * Upserts the device identity. Used at login with the real device profile, and at logout
 * with a cleared `uuid` -- which is how the concurrent-login check is released, since its
 * `profile.uuid &&` guard short-circuits on an empty value.
 */
export const upsertLoggedDevice = async (authToken: string, device: WebDeviceProfile): Promise<void> => {
  const response = await fetch(NIMBLY_API_USER_LOGGED_DEVICE(APP_ID), {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      authorization: authToken
    },
    body: JSON.stringify({ ...device, nimblyAppType: AppTypeEnum.NIMBLY_APP })
  });

  if (!response.ok) {
    const body = await response.json().catch(() => ({}));
    throw new Error(body?.error || body?.message || `logged device write failed (${response.status})`);
  }
};

/**
 * The uuid currently recorded against the user, or '' when none is.
 *
 * The endpoint returns the uuid alone rather than the whole device record, which is all the
 * concurrent-login check compares -- nothing in this client reads `deviceInfo`.
 */
export const fetchLoggedDeviceUUID = async (authToken: string): Promise<string> => {
  const response = await fetch(NIMBLY_API_USER_LOGGED_DEVICE(APP_ID), {
    method: 'GET',
    headers: { authorization: authToken }
  });

  if (!response.ok) {
    const body = await response.json().catch(() => ({}));
    throw new Error(body?.error || body?.message || `logged device read failed (${response.status})`);
  }

  const body = (await response.json().catch(() => ({}))) as { data?: string };
  return body?.data || '';
};

/**
 * Records the device against the signed-in user, resolving the id token itself.
 *
 * Failure-tolerant on purpose. All three call sites replace a fire-and-forget RTDB write:
 * two are logout paths where throwing would leave the user half signed out, and the third
 * is a successful login that must not be blocked by device bookkeeping. A failure leaves
 * the previous uuid in place, and the concurrent-login check fails safe either way.
 */
const recordDevice = async (device: WebDeviceProfile): Promise<void> => {
  try {
    const currentUser = firebase.auth().currentUser;
    if (!currentUser) {
      // eslint-disable-next-line no-console
      console.warn('[loggedDevice] no currentUser; skipping device write');
      return;
    }
    const authToken = await currentUser.getIdToken();
    await upsertLoggedDevice(authToken, device);
  } catch (error) {
    // Not rethrown -- see above -- but not silent either: a device write that fails
    // without a trace is how the concurrent-login prompt starts misfiring unexplained.
    // eslint-disable-next-line no-console
    console.error('[loggedDevice] device write failed', error);
  }
};

/** Records the device at login. */
export const recordLoggedDevice = (device: WebDeviceProfile): Promise<void> => recordDevice(device);

/**
 * Releases the device at logout by clearing the uuid, which is what frees the next login
 * from the concurrent-login prompt. Must be called before signing out, while the id token
 * can still be minted.
 */
export const clearLoggedDevice = (): Promise<void> => recordDevice({ uuid: '' });
